// components/products/ProductForm.tsx (modifié)
import { useState, useEffect } from 'react';
import { Product, Category, ProductFormData } from '@cabukaka/types';
import { Image as ImageIcon, Trash2 } from 'lucide-react';
import VariantsForm from './VariantsForm';

interface ProductFormProps {
  initialData?: Partial<Product>;
  categories: Category[];
  onSubmit: (data: ProductFormData) => Promise<void>;
  isSubmitting?: boolean;
}

interface FormErrors {
  name?: string;
  categoryId?: string;
  originalPrice?: string;
  price?: string;
  images?: string;
  stock?: string;
}

export default function ProductForm({
  initialData,
  categories,
  onSubmit,
  isSubmitting = false
}: ProductFormProps) {
  const [formData, setFormData] = useState<ProductFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    priceMode: initialData?.priceMode || 'fixed',
    unit: initialData?.unit || 'piece',
    categoryId: initialData?.categoryId || '',
    subcategoryId: initialData?.subcategoryId || '',
    originalPrice: initialData?.originalPrice || '',
    price: initialData?.price,
    inStock: initialData?.inStock ?? true,
    localImages: [],
    variantsFormData: initialData?.variantsFormData || [],
    stock: initialData?.stock || 0
  });

  const [stock, setStock] = useState<number>(initialData?.stock || 0);
  const [existingImages, setExistingImages] = useState<string[]>(initialData?.images || []);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    const urls = formData.localImages.map(file => URL.createObjectURL(file));
    setImagePreviewUrls(urls);
    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [formData.localImages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value ? parseFloat(value) : undefined }));
  };

  const handleStockChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setStock(isNaN(value) ? 0 : value);
    if (errors.stock) {
      setErrors(prev => ({ ...prev, stock: undefined }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length + formData.localImages.length + existingImages.length > 5) {
      setErrors(prev => ({ ...prev, images: 'Maximum 5 images' }));
      return;
    }
    setFormData(prev => ({ ...prev, localImages: [...prev.localImages, ...files] }));
    setErrors(prev => ({ ...prev, images: undefined }));
  };

  const removeNewImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      localImages: prev.localImages.filter((_, i) => i !== index)
    }));
  };

  const removeExistingImage = (index: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name) newErrors.name = 'Nom du produit requis';
    if (!formData.categoryId) newErrors.categoryId = 'Catégorie requise';
    if (formData.priceMode === 'fixed') {
      if (!formData.price && formData.price !== 0) newErrors.price = 'Prix requis';
      if (stock < 0) newErrors.stock = 'Stock invalide';
    }
    if (formData.localImages.length === 0 && existingImages.length === 0) {
      newErrors.images = 'Au moins une image requise';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData: ProductFormData = {
      ...formData,
      localImages: formData.localImages
    };

    if (formData.priceMode === 'fixed') {
      (submitData as any).stock = stock;
    }

    await onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Images du produit */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Images du produit *
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {existingImages.map((url, idx) => (
            <div key={`existing-${idx}`} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
              <img src={url} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeExistingImage(idx)}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}

          {imagePreviewUrls.map((url, idx) => (
            <div key={`new-${idx}`} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
              <img src={url} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeNewImage(idx)}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}

          {formData.localImages.length + existingImages.length < 5 && (
            <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-1 bg-gray-50 hover:bg-orange-50">
              <ImageIcon className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-500">Ajouter</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
            </label>
          )}
        </div>
        {errors.images && <p className="text-red-500 text-xs mt-1">{errors.images}</p>}
        <p className="text-xs text-gray-500 mt-2">Maximum 5 images. Formats: JPG, PNG, WebP</p>
      </div>

      {/* Informations générales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom du produit *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
            placeholder="Ex: iPhone 14 Pro"
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
          <select
            name="categoryId"
            value={formData.categoryId}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.categoryId ? 'border-red-300' : 'border-gray-300'}`}
          >
            <option value="">Sélectionner une catégorie</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          {errors.categoryId && <p className="text-red-500 text-xs mt-1">{errors.categoryId}</p>}
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          rows={4}
          value={formData.description}
          onChange={handleInputChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
          placeholder="Description détaillée du produit..."
        />
      </div>

      {/* Unité */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Unité de vente</label>
        <div className="flex gap-4">
          {['kg', 'liter', 'piece'].map(unit => (
            <label key={unit} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="unit"
                value={unit}
                checked={formData.unit === unit}
                onChange={handleInputChange}
                className="w-4 h-4 text-orange-600 focus:ring-orange-500"
              />
              <span>{unit === 'kg' ? 'Kilogramme' : unit === 'liter' ? 'Litre' : 'Pièce'}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Mode de prix */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Mode de prix</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="priceMode"
              value="fixed"
              checked={formData.priceMode === 'fixed'}
              onChange={handleInputChange}
              className="w-4 h-4 text-orange-600 focus:ring-orange-500"
            />
            <span>Prix fixe</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="priceMode"
              value="variant"
              checked={formData.priceMode === 'variant'}
              onChange={handleInputChange}
              className="w-4 h-4 text-orange-600 focus:ring-orange-500"
            />
            <span>Variantes</span>
          </label>
        </div>
      </div>

      {/* Prix fixe */}
      {formData.priceMode === 'fixed' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix original</label>
              <input
                type="text"
                name="originalPrice"
                value={formData.originalPrice}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                placeholder="Prix barré (optionnel)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix de vente *</label>
              <input
                type="number"
                name="price"
                value={formData.price || ''}
                onChange={handleNumberChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.price ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="0"
                step="1"
              />
              {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantité en stock *</label>
            <input
              type="number"
              value={stock}
              onChange={handleStockChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.stock ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="0"
              min="0"
              step="1"
            />
            {errors.stock && <p className="text-red-500 text-xs mt-1">{errors.stock}</p>}
            <p className="text-xs text-gray-500 mt-1">Nombre d'unités disponibles à la vente</p>
          </div>
        </>
      )}

      {/* Variantes */}
      {formData.priceMode === 'variant' && (
        <VariantsForm
          variants={formData.variantsFormData || []}
          onChange={(variants) => setFormData(prev => ({ ...prev, variantsFormData: variants }))}
        />
      )}

      {/* Disponibilité */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="inStock"
            checked={formData.inStock}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
          />
          <span className="text-sm text-gray-700">Produit visible et disponible à la vente</span>
        </label>
      </div>

      {/* Bouton de soumission */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition disabled:opacity-50"
        >
          {isSubmitting ? 'Enregistrement...' : (initialData?.id ? 'Modifier' : 'Créer')}
        </button>
      </div>
    </form>
  );
}